import 'styles/global.css'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { ContextDTO } from 'lib/contextCreation'
import { UserContext } from 'lib/context'
import { Context } from 'lib/api'
import Head from 'components/app/Head'
import YandexMetrica from 'components/app/YandexMetrica'
import Sentry from 'components/app/Sentry'
import Notifications from 'components/app/Notifications'
import { enableMapSet } from 'immer'
import BrowserCheck from 'components/app/BrowserCheck'
import SidebarContainer from 'components/sidebar/SidebarContainer'
import Footer from 'components/app/Footer'
import Events from 'components/app/Events'
import ModalDispatcher from 'components/app/ModalDispatcher'
import NotificationArea from 'components/app/NotificationArea'
import 'core-js/modules/es.array.at'
import 'core-js/modules/es.string.at-alternative'
import 'core-js/modules/es.array.to-sorted'
import 'core-js/modules/es.array.to-reversed'
import * as localePolyfill from 'lib/localePolyfill'
localePolyfill
import { useRouter } from 'next/router'
import MallAnnouncement from 'components/app/MallAnnouncement'
import { c } from 'lib/component-utils'
import SellmonitorFooter from 'components/app/SellmonitorFooter'
import FacebookMetrics from 'components/app/FacebookMetrics'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import SellematicsFooter from 'components/app/SellematicsFooter'
import { match } from 'ts-pattern'
import InterceptRouter from 'components/app/InterceptRouter'
import { motion } from 'motion/react'
import utc from 'dayjs/plugin/utc'
import dayjs from 'dayjs'
import Amplitude from 'components/app/Amplitude'
import ClientOnly from 'components/base/ClientOnly'
import relativeTime from 'dayjs/plugin/relativeTime'
import ru from 'dayjs/locale/ru'
import en from 'dayjs/locale/en'
import uz from 'dayjs/locale/uz'
import zh from 'dayjs/locale/zh'
import { useSetUserActivity } from 'lib/ai/currentUserActivity'
import CookieConsent from 'components/app/CookieConsent'
import I18nContext from 'components/app/I18nContext'
import { useThemeInitializer } from 'lib/colors'
import DocumentNotifier from 'components/app/DocumentNotifier'
import NotifiersContainer from 'components/app/NotifiersContainer'

const AssistantDock = dynamic(() => import('components/app/AssistantDock'), { ssr: false })
const PageLoadProgressBar = dynamic(() => import('components/app/PageLoadProgressBar'), {
  ssr: false,
})

dayjs.extend(relativeTime)
dayjs.extend(utc)

enableMapSet()

export type PageAppProps = {
  ctx: ContextDTO
  isResponsive?: boolean
  isFullWidthContainer?: boolean
  disableDarkTheme?: boolean
  disableSidebar?: boolean
  disableFooter?: boolean
  locale: { locale: string; messages: any }
}

function NextApp(props: AppProps<PageAppProps>) {
  const { pageProps, Component } = props
  const { err } = props as any
  const {
    ctx,
    isResponsive,
    isFullWidthContainer = false,
    disableDarkTheme = false,
    disableSidebar,
    disableFooter,
    locale,
  } = pageProps
  const router = useRouter()

  useThemeInitializer((ctx?.isAuthorized ?? false) && !disableDarkTheme)

  useSetUserActivity(['/'], {
    description: 'Пользователь смотрит какую-то страницу по маркетплейсу',
    mallCode: ctx?.currentMall?.code,
    mallName: ctx?.currentMall?.description,
  })

  if (!ctx) {
    return (
      <main>
        <Component {...pageProps} err={err} />
      </main>
    )
  }

  dayjs.locale(
    match(ctx.lang)
      .with('ru', () => ru)
      .with('en', () => en)
      .with('uz', () => uz)
      .with('zh', () => zh)
      .otherwise(() => en)
  )

  return (
    <I18nContext locale={locale.locale as any} messages={locale.messages}>
      <TooltipProvider delayDuration={50}>
        <UserContext.Provider value={new Context(ctx)}>
          {ctx && (
            <>
              {process.env.NODE_ENV === 'production' && ctx.theme.yandexMetrica && (
                <YandexMetrica />
              )}
              {process.env.NODE_ENV === 'production' && ctx.theme.facebookMetrics && (
                <FacebookMetrics />
              )}
              {process.env.NEXT_PUBLIC_MERCURE_USER_URL && ctx.isAuthorized && <Events />}
              {process.env.NEXT_PUBLIC_SENTRY_ENABLED === '1' && <Sentry />}
              <Head isResponsive={isResponsive} />
              <ClientOnly>{process.env.NODE_ENV === 'production' && <Amplitude />}</ClientOnly>
              <CookieConsent />
            </>
          )}
          <NotifiersContainer />
          {ctx?.isAuthorized && <DocumentNotifier />}
          <InterceptRouter />
          <BrowserCheck />
          <ModalDispatcher />
          <Notifications />
          <NotificationArea />
          <PageLoadProgressBar />
          <div className={c`w-full ${ctx?.isAuthorized} bg-gray-100`}>
            <div
              className={c`flex w-full mx-auto relative
                ${ctx?.isAuthorized && !isFullWidthContainer} max-w-[1920px]
                ${!isResponsive} min-w-[1170px]
              `}
            >
              {ctx?.isAuthorized && !disableSidebar && <SidebarContainer />}
              <main className="flex flex-1 min-w-0 flex-col justify-between min-h-screen">
                {ctx.currentMall?.notification && router.asPath.includes(ctx.currentMall?.code) && (
                  <MallAnnouncement />
                )}
                <motion.div
                  className="bg-white"
                  key={router.asPath.split('/').slice(0, 2).join('/')}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } }}
                  exit={{ opacity: 0, transition: { duration: 0, ease: 'easeIn' } }}
                >
                  <Component {...pageProps} />
                </motion.div>
                {match(ctx.theme.hostType)
                  .when(
                    () => disableFooter,
                    () => null
                  )
                  .with('sellematics', () => <SellematicsFooter />)
                  .with('sellmonitor', () => <SellmonitorFooter />)
                  .with('mayak', () => <SellmonitorFooter />)
                  .otherwise(() => (
                    <Footer />
                  ))}
              </main>
            </div>
          </div>
          <AssistantDock />
        </UserContext.Provider>
      </TooltipProvider>
    </I18nContext>
  )
}
export default NextApp
